import React from "react";
import { Typography, Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ForecastDrvDateRangePicker from "./ForecastDrvDateRangePicker";

const LOCKED_DATE_RANGE_REQUIRED_MSG = "Locked Gas Day date range is required.";

// ── Darker text for disabled/read-only fields ──────────────────────────────
// Targets the real MUI .Mui-disabled DOM class via a descendant selector.
// This only takes effect once the underlying TextField actually receives
// disabled={true} — see the `disabled={!isAdmin}` prop passed below.
// WebkitTextFillColor is included because Chrome/Safari paint disabled
// input text using that property instead of `color`.
const disabledFieldSx = {
  "& .Mui-disabled": {
    color: "#1a3a5c !important",
    WebkitTextFillColor: "#1a3a5c !important",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    color: "#1a3a5c !important",
    WebkitTextFillColor: "#1a3a5c !important",
  },
};

const ForecastDrvDetail = ({
  postedAt,
  lockedDateRange,
  onLockedDateRangeChange,
  isAdmin,
  dateRangeError,
  dateRangeHelperText,
}) => {
  return (
    <Box className="userInformationcontainer">
      <Accordion component="div" defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="forecast-drv-detail-content"
          id="forecast-drv-detail-header"
          component="div"
        >
          <Typography component="h2" className="userInformation">
            DRV FORECAST
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={0}>
            {/* ── Row 1: Report Name + Posted At ── */}
            <Grid size={{ xs: 12 }}>
              <Typography component="div" className="UserName">
                <Grid container spacing={3}>
                  <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                    <Typography component="span" className="textleft">
                      Washington Gas Five Day DRV Forecast
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                    <Typography component="span" className="textright">
                      {postedAt}
                    </Typography>
                  </Grid>
                </Grid>
              </Typography>
            </Grid>
            {/* ── Row 2: Locked for Gas Day(s) + Date Range Picker ── */}
            <Grid size={{ xs: 12 }}>
              <Typography component="div" className="UserName">
                <Grid container spacing={3} alignItems="center">
                  <Grid size={{ xs: 6, sm: 6, md: 6 }}>
                    <Typography component="span" className="textleft">
                      Forecast is Locked for Gas Day(s):
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 6, md: 6 }} sx={{ position: "relative", zIndex: 900 }}>
                    {/* Wrap in a pointer-events blocker when not admin so the
                        calendar popup / icon button stay non-interactive even
                        though those aren't governed by MUI's disabled state. */}
                    <Box
                      sx={
                        !isAdmin
                          ? {
                              pointerEvents: "none",
                              userSelect: "none",
                              ...disabledFieldSx,
                            }
                          : disabledFieldSx
                      }
                    >
                      <ForecastDrvDateRangePicker
                        startDate={lockedDateRange?.startDate ?? null}
                        endDate={lockedDateRange?.endDate ?? null}
                        label="Select Lock Date Range"
                        onDateRangeChange={isAdmin ? onLockedDateRangeChange : undefined}
                        // Actually disable the underlying TextField when not
                        // admin. This is what gives it MUI's Mui-disabled
                        // class, which is required for disabledFieldSx above
                        // to have any effect, and also gets us the standard
                        // disabled look (lighter border, no focus ring) for free.
                        disabled={!isAdmin}
                        // This field must not silently keep a stale value
                        // when the admin half-picks a range and clicks
                        // away — opt in to clearing + parent sync.
                        clearIncompleteOnBlur
                        // Message shown immediately when the user picks only
                        // a start (or only an end) date and clicks away.
                        requiredErrorMessage={LOCKED_DATE_RANGE_REQUIRED_MSG}
                        // Fallback so the same message also shows if the
                        // admin clicks "Save & Send" without ever touching
                        // this field at all.
                        error={dateRangeError}
                        helperText={dateRangeHelperText ?? LOCKED_DATE_RANGE_REQUIRED_MSG}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
export default ForecastDrvDetail;